<template>
  <div
    class="relative flex flex-col bg-white dark:bg-neutral-900"
    v-bind="$attrs"
  >
    <div
      class="bg-inherit w-full px-2 min-h-16 gap-2 grid grid-cols-[1fr_auto_1fr] items-center"
    >
      <div>
        <NamiButton
          class="ml-auto"
          @buttonClick="() => (menuOpen = false)"
          :icon="IconX"
          buttonType="secondary"
          text
          pill
        />
      </div>
      <NuxtLink :to="`/${mappedLocale}`" class="flex items-center">
        <img
          class="block h-8 w-8"
          src="~/assets/namicomi-logo.svg"
          :alt="config.public.siteName"
        />
        <p
          class="ml-2 font-[Roboto] font-bold text-xl text-nami-deep-blue dark:text-neutral-100"
        >
          {{ config.public.siteName }}
        </p>
      </NuxtLink>
    </div>
    <div class="absolute z-10 top-20 w-full flex justify-center items-center">
      <transition
        enter-active-class="transition"
        enter-from-class="opacity-0 -translate-x-1"
        enter-to-class="opacity-100 translate-x-0"
        leave-active-class="transition"
        leave-to-class="opacity-0 translate-x-1"
        leave-from-class="opacity-100 translate-x-0"
      >
        <div
          v-if="showNotificationReadUpdatedMessage"
          class="flex items-center shadow-md shadow-black/20 gap-2 py-2 px-4 rounded-md text-green-600 text-sm bg-neutral-200 dark:bg-neutral-800 text-center"
        >
          <IconCheck class="w-5 h-5" />
          <span> Settings updated </span>
        </div>
      </transition>
    </div>
    <div
      class="overflow-y-auto overflow-x-hidden flex-grow space-y-4 sm:space-y-6 px-4 pb-4"
    >
      <div class="space-y-2">
        <div class="grid grid-cols-[1fr_3rem]">
          <div class="space-y-2">
            <h2 class="font-medium text-xl">You're reading</h2>
            <NuxtLink :to="seriesHref" class="font-medium text-nami-nami-blue">
              {{ seriesTitle }}
            </NuxtLink>
          </div>
          <ContextMenuList
            closeOnOutsideClick
            defaultPosition="bottom-left"
            class="relative"
            :options="[
              {
                name: 'Copy link to page',
                icon: IconLink,
                action: getPageLink,
              },
              {
                name: 'Report',
                icon: IconFlag,
                action: () => {
                  if (!user) appStore?.openLoginRequiredModal();
                  else showReportModal = true;
                },
              },
            ]"
          >
            <template v-slot="{ isOpen, open, close }">
              <NamiButton
                @buttonClick="() => (isOpen ? close() : open())"
                buttonType="secondary"
                :icon="IconDots"
                :text="!isOpen"
              />
            </template>
          </ContextMenuList>
        </div>
        <div class="space-y-2" v-if="!solo">
          <h2 class="font-medium text-xl">Chapter</h2>
          <Reader2ChapterSelect
            :source-reading-direction
            :reader-manager="readerManager"
            :reader-settings="readerSettings"
            @switch-chapter="(v) => emit('switchChapter', v)"
            @next-chapter="emit('nextChapter')"
            @prev-chapter="emit('prevChapter')"
            :loading="navLoading || pending"
          />
        </div>
        <NamiButton
          v-if="!solo"
          @buttonClick="readerSettings.toggleComments"
          :icon="commentsOpen ? IconChevronRight : IconChevronLeft"
          :buttonType="commentsOpen ? 'primary' : 'secondary'"
          block
        >
          Comments
        </NamiButton>
        <SelectSingleList
          v-if="currentChapterVariants.length > 1 && !solo"
          class="!mt-4"
          label="Available languages"
          :modelValue="currentChapterId"
          :options="currentChapterVariants"
          :disabled="navLoading || pending"
          @update:modelValue="(v) => v && switchChapter(v)"
        />
      </div>
      <div class="space-y-4">
        <h2 class="font-medium text-xl">Reader Profile</h2>
        <div class="grid grid-cols-[1fr_auto] gap-2">
          <SelectSingleList
            v-model="currentProfile"
            :options="
              availableProfiles.map((profile) => ({
                text: profile.profile,
                value: profile.profile,
              }))
            "
          />
          <Tooltip
            class="flex items-center"
            variant="contrast"
            text="Reset to defaults"
          >
            <NamiButton
              buttonType="secondary"
              text
              block
              :icon="IconRefresh"
              @buttonClick="reset"
            />
          </Tooltip>
        </div>
      </div>
      <div class="space-y-2">
        <h2 class="font-medium text-xl" v-if="!isVls">Layout</h2>
        <div
          v-if="!isVls"
          class="grid grid-cols-[1fr_1fr] gap-2"
          :class="!advanced && 'grid-cols-[1fr_1fr_1fr]'"
        >
          <NamiButton
            @buttonClick="
              () =>
                advanced
                  ? updateReadingMode('single')
                  : readerSettings.applyPreset(AvailablePresets.SINGLE)
            "
            :icon="IconFile"
            :buttonType="
              (advanced && readingMode === 'single') ||
              currentPreset === AvailablePresets.SINGLE
                ? 'primary'
                : 'secondary'
            "
          >
            Single Page
          </NamiButton>
          <NamiButton
            @buttonClick="
              () =>
                advanced
                  ? updateReadingMode('double')
                  : readerSettings.applyPreset(AvailablePresets.DOUBLE)
            "
            :icon="IconBook"
            :buttonType="
              (advanced && readingMode === 'double') ||
              currentPreset === AvailablePresets.DOUBLE
                ? 'primary'
                : 'secondary'
            "
          >
            Double Page
          </NamiButton>
          <NamiButton
            v-if="!advanced"
            @buttonClick="
              readerSettings.applyPreset(AvailablePresets.LONGSTRIP)
            "
            :icon="IconLayoutRows"
            :buttonType="
              currentPreset === AvailablePresets.LONGSTRIP
                ? 'primary'
                : 'secondary'
            "
          >
            Long Strip
          </NamiButton>
        </div>
        <TransitionAutoHeight v-if="!isVls" :modelValue="advanced">
          <div class="grid grid-cols-[1fr_1fr] gap-2">
            <NamiButton
              @buttonClick="
                () => ((scrollDirection = 'horizontal'), emit('readingUpdated'))
              "
              :icon="IconColumns2"
              :buttonType="
                scrollDirection === 'horizontal' ? 'primary' : 'secondary'
              "
            >
              Horizontal
            </NamiButton>
            <NamiButton
              @buttonClick="
                () => ((scrollDirection = 'vertical'), emit('readingUpdated'))
              "
              :icon="IconLayoutRows"
              :buttonType="
                scrollDirection === 'vertical' ? 'primary' : 'secondary'
              "
            >
              Vertical
            </NamiButton>
          </div>
          <div class="grid grid-cols-[1fr_1fr_1fr] gap-2 mt-2">
            <NamiButton
              @buttonClick="changeDirection('content')"
              :buttonType="
                readingDirection === 'content' ? 'primary' : 'secondary'
              "
              block
            >
              Follow Content
            </NamiButton>
            <NamiButton
              @buttonClick="changeDirection('ltr')"
              :buttonType="readingDirection === 'ltr' ? 'primary' : 'secondary'"
              block
            >
              Left to Right
            </NamiButton>
            <NamiButton
              @buttonClick="changeDirection('rtl')"
              :buttonType="readingDirection === 'rtl' ? 'primary' : 'secondary'"
              block
            >
              Right to Left
            </NamiButton>
          </div>
        </TransitionAutoHeight>
        <div class="flex justify-between">
          <h2 class="font-medium text-xl my-auto">Image fit</h2>
          <div class="flex gap-2">
            <NamiButton
              :icon="IconMaximize"
              :buttonType="imageFit.current === 'fit' ? 'primary' : 'secondary'"
              @buttonClick="updateImageFit('fit')"
            >
              Fit
            </NamiButton>
            <NamiButton
              :icon="IconSettings"
              :buttonType="
                imageFit.current === 'custom' ? 'primary' : 'secondary'
              "
              @buttonClick="updateImageFit('custom')"
            />
          </div>
        </div>
        <div
          :class="{
            'opacity-50 pointer-events-none': imageFit.current === 'fit',
          }"
        >
          <h3 class="mb-2">Maximum width</h3>
          <NamiSliderNew
            v-model="maxWidth"
            @update:modelValue="() => emit('widthDragUpdate')"
            @dragStart="() => emit('widthDragDown')"
            @dragEnd="() => emit('widthDragUp')"
            :min="25"
            :max="100"
            suffix="%"
            continuous
            thin
          >
            <template #label-tooltip="{ sliderProgress }">
              {{ sliderProgress.toFixed(0) }}%
            </template>
          </NamiSliderNew>
        </div>
        <TransitionAutoHeight :modelValue="advanced">
          <div class="grid grid-cols-[1fr_auto] gap-y-2 font-medium text-xl">
            <h2>Stretch Images</h2>
            <NamiSwitch
              :model-value="upscaleSmallPages"
              @update:modelValue="updateUpscaleSmallPages"
            />
            <h2>Animation</h2>
            <NamiSwitch
              :model-value="forceDisableAnimation ? false : animation"
              @update:modelValue="changeAnimation"
              :disabled="forceDisableAnimation"
            />
            <h2>Reader Hint</h2>
            <NamiSwitch
              :model-value="!readerSettings.hideReadingHint"
              @update:modelValue="(v) => (readerSettings.hideReadingHint = !v)"
            />
            <!--            TODO: end life or potentially revive after swiper is replaced-->
            <!--            <template v-if="!isVls">-->
            <!--              <h2>Continuous</h2>-->
            <!--              <NamiSwitch-->
            <!--                v-model="continuous"-->
            <!--                @update:modelValue="updateSettings"-->
            <!--              />-->
            <!--            </template>-->
          </div>
          <NamiDivider class="mt-3" />
          <div class="my-2">
            <h2 class="font-medium text-xl">Scroll wheel behavior</h2>
            <RadioGroup
              :modelValue="scrollWheelBehavior"
              @update:modelValue="
                (v) =>
                  changeScrollWheelBehavior(v as 'pageTurn' | 'zoom' | 'none')
              "
              :options="[
                { name: 'Zoom', value: 'zoom' },
                { name: 'Turn page', value: 'pageTurn' },
                { name: 'None', value: 'none' },
              ]"
              horizontal
            />
          </div>
        </TransitionAutoHeight>
        <NamiButton
          class="mt-2"
          button-type="secondary"
          text
          pill
          :icon="advanced ? IconChevronUp : IconChevronDown"
          @button-click="advanced = !advanced"
          block
          >Advanced
        </NamiButton>
      </div>
      <Reader2DrawerCTA v-if="title && !solo" />
    </div>
  </div>
  <ReportModal
    v-model="showReportModal"
    entityType="chapter"
    :entityId="currentChapterId"
  />
</template>

<script setup lang="ts">
import {
  IconBook,
  IconCheck,
  IconChevronDown,
  IconChevronLeft,
  IconChevronRight,
  IconChevronUp,
  IconColumns2,
  IconDots,
  IconFile,
  IconFlag,
  IconLayoutRows,
  IconLink,
  IconMaximize,
  IconRefresh,
  IconSettings,
  IconX,
} from "@tabler/icons-vue";
import { useWindowSize } from "@vueuse/core";
import { parseTitleForDisplay } from "~/utils/display/title";
import {
  type ImageFit,
  isTitle,
  type ReadingDirection,
  type ReadingMode,
  type TitleReadingMode,
} from "~/src/api";
import type { UnwrapNestedRefs } from "vue";
import { ReaderManager } from "~/src/reader/managers/reader";
import { isContentPageGroup, isImagePageGroup } from "~/src/reader/utils";
import {
  AvailablePresets,
  type ReaderSettingsInterface,
} from "~/src/reader/static";
import {
  getReaderTitle,
  getReaderTitleChapters,
} from "~/src/reader/managers/title";
import { getReaderChapter } from "~/src/reader/managers/chapter";
import { useQueryClient } from "@tanstack/vue-query";
import {
  getAvailableLanguageVariants,
  getChapterSequenceForLang,
} from "~/src/reader/generic";

interface Props {
  sourceReadingDirection: TitleReadingMode;
  readerManager: UnwrapNestedRefs<ReaderManager>;
  readerSettings: UnwrapNestedRefs<ReaderSettingsInterface>;
  navLoading?: boolean;
  solo?: boolean;
}

interface Emits {
  (e: "readingUpdated"): void;

  (e: "switchChapter", v: string): void;

  (e: "nextChapter"): void;

  (e: "prevChapter"): void;

  // this is here to communicate that width is being changed so we can adjust vls accordingly
  (e: "widthDragDown"): void;

  (e: "widthDragUpdate"): void;

  (e: "widthDragUp"): void;
}

const { sourceReadingDirection, readerManager, readerSettings } =
  defineProps<Props>();
const emit = defineEmits<Emits>();
const config = useRuntimeConfig();
const nuxtApp = useNuxtApp();
const href = useHref();
const queryClient = useQueryClient();

const { locale, t: translate } = useI18n();
const mappedLocale = computed(() => reverseLocaleMap(locale.value));
const authStore = nuxtApp.$auth();
const appStore = nuxtApp.$app();
const settingsStore = nuxtApp.$settings();
const user = computed(() => authStore?.user);

const { width: windowWidth } = useWindowSize();
const showReportModal = ref(false);

const currentChapterId = computed(() => readerManager.getCurrChapterId());
const { data: chapterData } = getReaderChapter(
  () => queryClient,
  currentChapterId,
);
const fetchedTitleId = computed(
  () => chapterData.value?.relationships.find(isTitle)?.id,
);
const { data: title } = getReaderTitle(() => queryClient, fetchedTitleId);
const { data: aggregate } = getReaderTitleChapters(
  () => queryClient,
  fetchedTitleId,
);

const { title: seriesTitle, href: seriesHref } = parseTitleForDisplay(title);

const isVls = computed(() => sourceReadingDirection === "vls");
const currentChapterVariants = computed(() => {
  if (!aggregate.value) return [];
  const variants = getAvailableLanguageVariants(
    "chapters",
    aggregate.value,
    currentChapterId.value,
  );
  return variants
    .map((variant) => ({
      text: translate(`languages.${localeMap(variant.translatedLanguage)}`),
      value: variant.id,
    }))
    .sort((a, b) => a.text.localeCompare(b.text));
});

const { action: switchChapter, pending } = useAction2(async (v: string) => {
  reloadNuxtApp({ path: href.value.chapter.id(v), force: true });
  // TODO: reimplement in-place chapter language switching
  // if (!aggregate.value) return;
  // const variants = getAvailableLanguageVariants("chapters", aggregate.value, v);
  //
  // const newLang = variants.find((ch) => ch.id === v)?.translatedLanguage;
  // if (!newLang) return;
  //
  // const sequence = getChapterSequenceForLang(
  //   "chapters",
  //   aggregate.value,
  //   newLang,
  // )?.map((ch) => ch.id);
  // readerManager.updateChapterIdSequence(sequence);
  // await readerManager.fetchChapterPages(() => queryClient, v);
  // readerManager.currentPageIndex = readerManager.pageArray.findIndex(
  //   (page) => page.chapterId === v && isContentPageGroup(page),
  // );
  // readerManager.loadChapterPages(v);
});

const { action: getPageLink } = useAction2(async () => {
  const currentPage = readerManager.pageArray[readerManager.currentPageIndex];
  if (isImagePageGroup(currentPage)) {
    const src = currentPage.pages[0].serverSrc;
    const data = readerManager.rawPageMap.get(currentPage.chapterId)?.pages;

    const index =
      data?.[readerManager.quality].findIndex(
        (page) => page.imageUrl === src,
      ) ?? 0;
    const imageUrl = data?.source[index].imageUrl ?? "";
    const regex = /(?:high|medium|low|source)\/\d+-(?<fileName>\w+?)\.\w+/;
    const match = regex.exec(imageUrl);
    if (match) {
      const { fileName } = match.groups!;
      await copyTextToClipboard(
        `${config.public.baseUrl}/en/chapter/${
          currentPage.chapterId
        }?sp=${fileName.substring(0, 12)}`,
      );
      return;
    }
  }

  await copyTextToClipboard(`https://nami.moe/c/${currentPage.chapterId}`);
});

// settings
const {
  scrollWheelBehavior,
  scrollDirection,
  readingMode,
  readingDirection,
  imageFit,
  upscaleSmallPages,
  currentProfileIndex,
  animation,
  menuOpen,
  commentsOpen,
} = toRefs(readerSettings);

const currentPreset = computed(() => readerSettings.getMatchingPresets()[0]);
const availableProfiles = computed(() =>
  readerSettings.getAvailableProfiles({
    "title.readingMode": sourceReadingDirection,
    "screen.width": window.innerWidth,
  }),
);

const forceDisableAnimation = computed(() => {
  // We're disabling animation on small screens for high preset to prevent crashes
  return (
    settingsStore?.settings.site.quality === "data" && windowWidth.value < 640
  );
});

const advanced = ref(false);
const showNotificationReadUpdatedMessage = ref(false);

readerSettings.onSettingsUpdate(() => {
  showNotificationReadUpdatedMessage.value = true;

  setTimeout(() => {
    showNotificationReadUpdatedMessage.value = false;
  }, 2000);
});

const currentProfile = computed<string>({
  get() {
    return availableProfiles.value[currentProfileIndex.value].profile;
  },
  set(v) {
    readerSettings.applyProfile(v, {
      "title.readingMode": sourceReadingDirection,
      "screen.width": window.innerWidth,
    });
  },
});

function updateReadingMode(newReadingMode: ReadingMode) {
  readingMode.value = newReadingMode;
}

function updateImageFit(newImageFit: ImageFit) {
  imageFit.value = {
    ...imageFit.value,
    current: newImageFit,
  };
}

const maxWidth = computed({
  get: () => imageFit.value.custom.maximumWidth * 100,
  set: (newVal) => {
    imageFit.value = {
      ...imageFit.value,
      custom: { maximumWidth: Math.floor(newVal) / 100 },
    };
  },
});

function updateUpscaleSmallPages(newUpscaleSmallPages: boolean) {
  upscaleSmallPages.value = newUpscaleSmallPages;
}

function changeDirection(newDirection: ReadingDirection) {
  readingDirection.value = newDirection;
  emit("readingUpdated");
}

function changeAnimation(newAnimation: boolean) {
  animation.value = newAnimation;
}

function changeScrollWheelBehavior(
  newScrollWheelBehavior: "pageTurn" | "zoom" | "none",
) {
  scrollWheelBehavior.value = newScrollWheelBehavior;
}

async function reset() {
  const action = await appStore?.prompt(
    "Would you like to reset this profile's settings back to default?",
    {
      icon: "confused",
      forceCloseKey: "cancel",
      buttons: {
        cancel: {
          buttonType: "secondary",
          buttonText: "Cancel",
        },
        confirm: {
          buttonType: "danger",
          buttonText: "Reset",
        },
      },
    },
  );

  if (action !== "confirm") {
    return;
  }

  const profileToBeReset =
    availableProfiles.value[currentProfileIndex.value]?.profile;
  if (!profileToBeReset) return;

  readerSettings.resetProfile(profileToBeReset);
}
</script>
